/**
 * @author       yontaek@zumper.com (Yon Taek Chung)
 * @copyright    Copyright (c) 2022, Zumper
 */

import {
  getFeaturedAndSpotlightLimits,
  selectLocation,
  selectUrl,
} from '@zcode-ui/web-zumper-global-state'
import { definitelyError, isValidBounds } from '@zcode/nr-utils'
import * as Api from '@zcode/nr/es/shared/api'
import Listable from '@zcode/nr/es/shared/bizos/listable'
import type {
  Dispatch,
  ExtraThunkArg,
  Filters,
  Listable as ListableType,
  Listables,
  RawListable,
  SaveSearchLocationBox,
} from '@zcode/state-type'
import type { AnyAction } from 'redux-actions'
import type { ThunkDispatch } from 'redux-thunk'
import { trackFilterCount } from '../../../components/Filter/FilterComponents/PopularAmenitiesFilter/popularAmenitiesTracking'
import { getAPIFriendlyFilters, getInventorySearchFriendlyFilters } from '../../../helpers'
import { isDateValid } from '../../../helpers'
import { logger } from '../../../logger'
import type { GetState } from '../../types'
import {
  doneFetchingMatchingCount,
  setFilterMatchingCount,
  startFetchingMatchingCount,
} from '../actions'

interface QueryTypes extends Partial<Filters> {
  matching: boolean
  url?: string
  maxLat?: number
  minLat?: number
  maxLng?: number
  minLng?: number
  floorplans?: number
  vacation?: boolean
  shortTerm?: boolean
  longTerm?: boolean
}

interface FilterTypes extends Filters {
  buildingIds?: number[]
  listingIds?: number[]
}

export const dedupeByUid = (...arrays: Array<readonly ListableType[]>) => {
  const seen = new Set<string>()
  const result: Array<string> = []

  for (const array of arrays) {
    for (const item of array) {
      const { uId } = item

      if (!!uId && !seen.has(uId)) {
        seen.add(uId)
        result.push(uId)
      }
    }
  }

  return result
}

export const updateFilterMatchingCount =
  (filters: FilterTypes, box: SaveSearchLocationBox) =>
  async (dispatch: Dispatch, getState: GetState, { optimizely }: ExtraThunkArg) => {
    const state = getState()
    const url = selectUrl(state) ?? null
    const { box: destinationBox, destinationName } = selectLocation(state) ?? {}
    const { longTerm, startDate, endDate } = filters
    const { DEFAULT_SPOTLIGHT_LIMIT, DEFAULT_FEATURED_API_LIMIT } =
      getFeaturedAndSpotlightLimits(optimizely)

    const listablesParams = {
      ...filters,
      matching: true,
      sort: filters.sort,
      url,
    }

    // For destinations, we always want to use box params instead of url
    if (destinationName) {
      listablesParams.maxLat = destinationBox?.maxLat ?? null
      listablesParams.maxLng = destinationBox?.maxLng ?? null
      listablesParams.minLat = destinationBox?.minLat ?? null
      listablesParams.minLng = destinationBox?.minLng ?? null
    }

    if (isValidBounds(box)) {
      listablesParams.minLat = box.minLat
      listablesParams.maxLat = box.maxLat
      listablesParams.minLng = box.minLng
      listablesParams.maxLng = box.maxLng
    }
    // discard start/end date if invalid
    if (!isDateValid(startDate)) {
      listablesParams.startDate = null
    }
    if (!isDateValid(endDate)) {
      listablesParams.endDate = null
    }

    const listablesQuery = longTerm
      ? (getInventorySearchFriendlyFilters({
          filters: listablesParams,
        }) as QueryTypes)
      : getAPIFriendlyFilters({ filters: listablesParams })

    const spotlightQuery = getInventorySearchFriendlyFilters({
      filters: listablesParams,
      urlOnly: true,
    })

    let listablesPromises:
      | [
          listables: Promise<Listables>,
          featured: ReturnType<(typeof Api)['mapListFeatured']['query']>,
          spotlight?: ReturnType<(typeof Api)['maplistSpotlight']['query']>,
        ]
      | [listables: Promise<Listables>, featured?: never, spotlight?: never] = longTerm
      ? [
          Api.mapListSearch.query({
            ...listablesQuery,
            limit: 50,
            nearby: {
              limit: 25,
              threshold: 25,
            },
          }),
        ]
      : [Api.strListables.query(listablesQuery)]

    if (longTerm) {
      listablesPromises = [
        listablesPromises[0],
        Api.mapListFeatured.query({
          filters: { ...listablesQuery, limit: DEFAULT_FEATURED_API_LIMIT },
        }),
      ]
      if (spotlightQuery.url) {
        listablesPromises.push(
          Api.maplistSpotlight.query({
            filters: { ...spotlightQuery, limit: DEFAULT_SPOTLIGHT_LIMIT },
          })
        )
      }
    }

    dispatch(startFetchingMatchingCount())

    try {
      const [listables, featured, spotlight] = await Promise.all(listablesPromises)
      const { matching, nearby } = listables
      let augmentedSpotlightLisables: ListableType[] = []
      if (spotlight && 'listables' in spotlight) {
        augmentedSpotlightLisables = spotlight.listables.map((listable: RawListable) => {
          return Listable.fromJS({
            ...listable,
          })
        })
      }
      let augmentedFeaturedLisables: ListableType[] = []
      if (featured && 'listables' in featured) {
        augmentedFeaturedLisables = featured.listables.map((listable: RawListable) => {
          return Listable.fromJS({
            ...listable,
          })
        })
      }
      const dedupedListables = dedupeByUid(
        listables.listables,
        augmentedSpotlightLisables,
        augmentedFeaturedLisables
      )

      const totalCount = nearby?.matches
        ? matching -
          listables.listables.length +
          dedupedListables.length +
          nearby.matches.listables.length
        : matching - listables.listables.length + dedupedListables.length
      dispatch(doneFetchingMatchingCount())
      dispatch(setFilterMatchingCount(totalCount))
      dispatch(trackFilterCount(totalCount))
    } catch (error) {
      dispatch(doneFetchingMatchingCount())

      logger.warn(
        'error for needed listables',
        listablesQuery,
        definitelyError('error for needed listables', error)
      )
      return await Promise.reject(error)
    }
  }
